/* eslint-disable react/no-find-dom-node */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';
import { colors, hover } from 'utils/colors';
import { bytesToSize } from 'utils/helpers';

class FileSelectLarge extends Component {
  constructor(props) {
    super(props);
    this.state = { isDragging: false, error: '' };
    this.inputRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { selectedFile } = this.props;
    if (selectedFile !== prevProps.selectedFile) {
      this.render();
    }
  }

  onChange(e) {
    const { setSelectedFile } = this.props;
    const { files } = e.target;
    if (files && files[0]) {
      if (!this.isFileTypeAllowed(files[0])) {
        this.setState({ error: this.getErrorMessage() });
        return;
      }
      this.setState({ error: '' });
      setSelectedFile(files[0]);
    }
  }

  onInputClick = (event) => {
    event.target.value = '';
  };

  onBrowseClick = () => {
    if (this.inputRef.current) {
      this.inputRef.current.click();
    }
  };

  onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ isDragging: true, error: '' });
  };

  onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ isDragging: false });
  };

  onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { setSelectedFile } = this.props;
    const { files } = event.dataTransfer;

    if (files && files[0]) {
      if (!this.isFileTypeAllowed(files[0])) {
        this.setState({ isDragging: false, error: this.getErrorMessage() });
        return;
      }
      this.setState({ isDragging: false, error: '' });
      setSelectedFile(files[0]);
    } else {
      this.setState({ isDragging: false });
    }
  };

  getErrorMessage = () => {
    const { fileTypes } = this.props;
    return fileTypes
      ? `Unsupported file type. Accepted: ${fileTypes}`
      : 'Unsupported file type.';
  };

  // `accept`/`fileTypes` is a comma-separated list like ".pdf,.docx" or
  // "image/*,application/pdf". The browser only enforces this for the
  // native file picker, not for drag-and-drop, so we re-check manually.
  isFileTypeAllowed = (file) => {
    const { fileTypes } = this.props;
    if (!fileTypes) return true;

    const accepted = fileTypes
      .split(',')
      .map((type) => type.trim().toLowerCase())
      .filter(Boolean);

    if (accepted.length === 0) return true;

    const fileName = (file.name || '').toLowerCase();
    const fileMime = (file.type || '').toLowerCase();

    return accepted.some((type) => {
      if (type.startsWith('.')) {
        return fileName.endsWith(type);
      }
      if (type.endsWith('/*')) {
        return fileMime.startsWith(type.slice(0, -1));
      }
      return fileMime === type;
    });
  };

  render() {
    const { width, placeholder, selectedFile, setSelectedFile, fileTypes } =
      this.props;
    const { isDragging, error } = this.state;

    return (
      <Container>
        {selectedFile.name ? (
          <File>
            <div className="info">
              <div className="filename">{selectedFile.name}</div>
              <div className="subheader">{bytesToSize(selectedFile.size)}</div>
            </div>
            <RemoveButton
              type="button"
              onClick={() => setSelectedFile({})}
              aria-label="Remove file"
            >
              <Icon name="trash alternate outline" />
            </RemoveButton>
          </File>
        ) : (
          <DropZone
            width={width}
            isDragging={isDragging}
            hasError={!!error}
            onClick={this.onBrowseClick}
            onDragOver={this.onDragOver}
            onDragEnter={this.onDragOver}
            onDragLeave={this.onDragLeave}
            onDrop={this.onDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.onBrowseClick();
              }
            }}
          >
            <input
              ref={this.inputRef}
              type="file"
              id="fileupload"
              name="fileupload"
              accept={fileTypes}
              onChange={(e) => this.onChange(e)}
              onClick={this.onInputClick}
            />
            <IconBadge hasError={!!error}>
              <Icon name={error ? 'exclamation triangle' : 'upload'} />
            </IconBadge>
            <Title>{placeholder || 'Drag and drop your file here'}</Title>
            <Divider>or</Divider>
            <BrowseButton type="button">Browse Files</BrowseButton>
            {error && <ErrorText>{error}</ErrorText>}
          </DropZone>
        )}
      </Container>
    );
  }
}

FileSelectLarge.propTypes = {
  selectedFile: PropTypes.object.isRequired,
  setSelectedFile: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  width: PropTypes.string,
  fileTypes: PropTypes.string,
};

FileSelectLarge.defaultProps = {
  placeholder: 'Drag and drop your file here',
  width: '',
  fileTypes: '',
};

export default FileSelectLarge;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const ACCENT = colors.blue;
const ACCENT_HOVER = hover.blue;
const BADGE_BG = '#E9E8FC';
const PANEL_BG = '#F7F7FC';
const BORDER = '#D8D7EE';
const BORDER_ACTIVE = ACCENT;
const TEXT_DARK = '#1F2233';
const TEXT_MUTED = '#8A8AA3';
const ERROR = '#D64545';
const ERROR_BG = '#FDEDED';

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const DropZone = styled.div`
  width: ${(props) => props.width || '100%'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 40px 24px;
  background-color: ${(props) => {
    if (props.hasError) return ERROR_BG;
    return props.isDragging ? '#EFEEFD' : PANEL_BG;
  }};
  border: 2px dashed
    ${(props) => {
      if (props.hasError) return ERROR;
      return props.isDragging ? BORDER_ACTIVE : BORDER;
    }};
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${(props) => (props.hasError ? ERROR : BORDER_ACTIVE)};
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }

  & input[type='file'] {
    /*
     * Hide only visually, but have it available for screen readers:
     * https://snook.ca/archives/html_and_css/hiding-content-for-accessibility
     */
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    clip-path: inset(50%);
    border: 0;
  }
`;

const IconBadge = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background-color: ${(props) => (props.hasError ? ERROR_BG : BADGE_BG)};
  color: ${(props) => (props.hasError ? ERROR : ACCENT)};
  pointer-events: none;

  i.icon {
    margin: 0;
    font-size: 18px;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${TEXT_DARK};
  pointer-events: none;
`;

const Divider = styled.div`
  font-size: 16px;
  color: ${TEXT_MUTED};
  pointer-events: none;
`;

const BrowseButton = styled.button`
  appearance: none;
  border: none;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background-color: ${ACCENT};
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  pointer-events: none;

  &:hover {
    background-color: ${ACCENT_HOVER};
  }
`;

const ErrorText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${ERROR};
  pointer-events: none;
`;

const File = styled.div`
  width: 100%;
  padding: 14px 16px;
  font-size: 15px;
  background-color: ${PANEL_BG};
  border: 1px solid ${BORDER};
  border-radius: 12px;
  text-align: left;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  & .info {
    display: flex;
    flex-direction: column;
    row-gap: 0.4rem;
    min-width: 0;
  }

  & .filename {
    color: ${TEXT_DARK};
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .subheader {
    font-size: 12px;
    color: ${colors.grey || TEXT_MUTED};
  }
`;

const RemoveButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: ${TEXT_MUTED};
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  i.icon {
    margin: 0;
  }

  &:hover {
    color: #d64545;
    background-color: rgba(214, 69, 69, 0.08);
  }
`;
